import { ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography, useMediaQuery, Theme } from "@mui/material";
import { Investment } from "@rentablo/api/apiv1";
import { toCurrency } from "@helpers";
import { InvestmentTableAddQuoteModal } from "@features/investments/components/InvestmentTableAddQuoteModal";
import { AutoTable } from "@rentablo/components";
import { Modal, LoadingPlaceHolder } from "@rentablo/components";
import { useSecurityImages } from "@hooks";
import {
  useTableConfig,
  InvestmentTableAdditionalProps,
} from "./useTableConfig";
import { InvestmentDetailModal } from "./InvestmentDetailModal";
import { InvestmentListView, InvestmentMobileList } from "./mobile";

export interface Props {
  investments: Investment[];
  className?: string;
  isLoading?: boolean;
  isFetching?: boolean;
  isPending?: boolean;
  routeHandler?: (url: string) => void;
  formatCurrency?: (value: number, currency?: string) => string;
  // Sort and chip settings of the compact phone list; without it phones get the table.
  mobileView?: InvestmentListView;
  // Shown in the phone list's header line while it has entries, e.g. Export.
  mobileHeaderAction?: ReactNode;
  // Shown at the end of that line even when it is empty, so a filter that
  // leaves no entries can still be changed.
  mobileSettingsAction?: ReactNode;
}

export const InvestmentsTable = ({
  className,
  investments,
  isLoading,
  isFetching,
  isPending,
  routeHandler,
  formatCurrency = toCurrency,
  mobileView,
  mobileHeaderAction,
  mobileSettingsAction,
}: Props) => {
  const { t } = useTranslation();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [selectedInvestment, setSelectedInvestment] = useState<Investment>();

  const handleRowClick = useCallback((investment: Investment) => {
    setSelectedInvestment(investment);
  }, []);

  const handleCloseDetailModal = () => {
    setSelectedInvestment(undefined);
  };

  const config = useTableConfig(handleRowClick);

  const [currentAddQuoteInvestment, setCurrentAddQuoteInvestment] =
    useState<Investment>();
  const [isAddQuoteModalShown, setIsAddQuoteModalShown] = useState(false);

  const handleInvestmentSelected = (investment: Investment) => {
    setCurrentAddQuoteInvestment(investment);
    setIsAddQuoteModalShown(true);
  };

  const handleCloseModal = () => {
    setCurrentAddQuoteInvestment(undefined);
    setIsAddQuoteModalShown(false);
  };

  const securityForLogos = useMemo(
    () =>
      investments.map((investment) => ({
        hasValidIsin: true,
        type: investment.quoteProvider === "currency" ? "81_fcurr" : "11_stock",
        isin: investment.isin || investment.tickerSymbol,
        ticker_symbol: investment.tickerSymbol,
        quoteProvider: investment.quoteProvider,
      })),
    [investments]
  );

  const images = useSecurityImages(securityForLogos);

  const additionalProps = useMemo<InvestmentTableAdditionalProps>(
    () => ({
      formatCurrency,
      images,
      onRowClick: routeHandler,
      onClickAddQuoteModal: handleInvestmentSelected,
    }),
    [formatCurrency, images, routeHandler]
  );

  return (
    <>
      {isSm && isLoading ? (
        <LoadingPlaceHolder className={className} />
      ) : isSm && mobileView ? (
        <InvestmentMobileList
          className={className}
          investments={investments}
          images={images}
          view={mobileView}
          isFetching={isFetching}
          onRowClick={handleRowClick}
          onNameClick={routeHandler}
          onAddQuote={handleInvestmentSelected}
          headerAction={mobileHeaderAction}
          settingsAction={mobileSettingsAction}
        />
      ) : (
        <>
          {isSm && investments.length > 0 && (
            <Typography variant="body3" color="textSecondary" mb={1}>
              {t("domains.investments.investmentTable.entries", {
                count: investments.length,
              })}
            </Typography>
          )}
          <AutoTable
            settings={config}
            data={investments}
            isPending={isPending}
            isFetching={isFetching}
            className={className}
            additionalProps={additionalProps}
            sx={{
              "& tbody tr:first-of-type .MuiTableCell-root": {
                paddingTop: (theme: Theme) => theme.spacing(4),
              },
            }}
          />
        </>
      )}

      <Modal
        isOpen={isAddQuoteModalShown}
        onClose={handleCloseModal}
        title={t("domains.investments.investmentTable.addQuote.modalTitle")}
        width={600}
      >
        <InvestmentTableAddQuoteModal
          investment={currentAddQuoteInvestment}
          onClose={handleCloseModal}
          currency={currentAddQuoteInvestment?.currency}
        />
      </Modal>

      <InvestmentDetailModal
        investment={selectedInvestment}
        isOpen={!!selectedInvestment}
        onClose={handleCloseDetailModal}
        image={
          selectedInvestment
            ? images[selectedInvestment.isin || ""] ||
              images[selectedInvestment.tickerSymbol || ""]
            : undefined
        }
      />
    </>
  );
};
